import { useEffect, useState } from 'react';
import style from './TelaCliente.module.css';

// Componentes
// Componentes
import BookingSlotPicker from './BookingSlotPicker';
import BookingConfirmationOverlay from './BookingConfirmationOverlay';
import Avatar from '../ui/Avatar';

// Ferramentas e Funções
import { useDataManagement } from '../ferramentas/RequisitarAPI.ts';
import { formatarDate } from '../ferramentas/formatarDate';
import ToastMessage from '../ferramentas/toastMessage/ToastMessage.js';

import { generatePrefillUrl } from '../../utils/prefillUtils.js';

// Componente principal de agendamento
function TelaCliente() {
    // Hooks de estado
    const [appointments, setAppointments] = useState({});
    const [modalState, setModalState] = useState({ isOpen: false, date: null });
    const [usersList, setUsersList] = useState([]); // Lista pura de usuários
    const [filteredUsers, setFilteredUsers] = useState([]); // Lista filtrada
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null); // Usuário selecionado (objeto completo)

    // Hooks de dados
    const { appointments: fetchAppointments, users, services: fetchServices, load } = useDataManagement();
    const [servicesList, setServicesList] = useState([]);

    // Carregar usuários e serviços ao iniciar
    useEffect(() => {
        async function loadData() {
            // Load Users
            const userResult = await users.listClientLevel();
            if (userResult.ok) {
                const sortedUsers = Object.values(userResult.data).sort((a, b) =>
                    a.fullname.localeCompare(b.fullname)
                );
                setUsersList(sortedUsers);
                setFilteredUsers(sortedUsers);
            }

            // Load Services
            const serviceResult = await fetchServices.list();
            if (serviceResult.ok && typeof serviceResult.data === 'object') {
                setServicesList(Object.values(serviceResult.data));
            }
        }
        loadData();
    }, [users, fetchServices]);

    // ... (keep search filter effect) ...
    // Filtrar usuários quando o termo de busca mudar
    useEffect(() => {
        const results = usersList.filter(user =>
            user.fullname.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredUsers(results);
    }, [searchTerm, usersList]);

    // Carregar calendário quando um usuário é selecionado
    useEffect(() => {
        if (!selectedUser?.id) {
            setAppointments({});
            return;
        }

        async function loadCalendar() {
            const result = await fetchAppointments.getCardsClientLevel(selectedUser.id);
            if (result.ok) {
                setAppointments(result.data);
            }
        }

        loadCalendar();
    }, [selectedUser, fetchAppointments]);

    // Handlers
    const handleUserSelect = (user) => {
        setSelectedUser(user);
    };

    const handleBackToGrid = () => {
        setSelectedUser(null);
        setAppointments({});
    };

    // New handler: open overlay when a slot is clicked
    const handleSlotSelect = (date, time) => {
        setModalState({ isOpen: true, date, time });
    };

    const handleConfirmBooking = ({ customerName, date, time, services }) => {
        if (!services || services.length === 0) {
            ToastMessage({ cod: "w", texto: 'Por favor, selecione ao menos um serviço.' });
            return;
        }

        const [h, m] = time.split(':');
        const dataObj = new Date(date);
        dataObj.setHours(h, m, 0, 0);

        const formattedDateISO = dataObj.toISOString();

        // 1. Prepare Service Data for Prefill
        // We use the first service ID for compatibility, but pass all names stringified
        const mainService = services[0];
        const combinedServiceNames = services.map(s => s.name).join(' + ');

        const prefillData = {
            serviceId: mainService.id, // Primary service ID for system compat
            serviceIds: services.map(s => s.id), // New field for multiple support
            serviceName: combinedServiceNames,
            userId: selectedUser.id,
            userFullname: selectedUser.fullname,
            date: formattedDateISO,
            customer: customerName
        };

        const prefillUrl = generatePrefillUrl(prefillData);

        // 2. Construct WhatsApp Message
        const numero = selectedUser?.phone || "5519997160195";
        const dataFormatada = `${formatarDate(dataObj).dia}/${formatarDate(dataObj).mes}`;

        // Calculate Totals
        const totalValue = services.reduce((acc, s) => acc + (Number(s.price) || 0), 0);

        // Service list for message
        const serviceListString = services.map(s => `• ${s.name}`).join('\n');

        const mensagem = `Olá, ${selectedUser?.fullname}!
Gostaria de agendar um horário.

*Serviços:*
${serviceListString}

*Info:*
Data: ${dataFormatada} às ${time}
Cliente: ${customerName}
Valor Total Estimado: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalValue)}

Confirmação:
${prefillUrl}`;

        const linkWhatsApp = `https://api.whatsapp.com/send?phone=${numero}&text=${encodeURIComponent(mensagem)}`;

        window.open(linkWhatsApp, '_blank');
        closeModal();
    };

    const closeModal = () => setModalState({ isOpen: false, date: null, time: null });

    return (
        <div className={style.page_container} id="clientCalendar">
            {/* O Cartão principal adapta sua largura se estiver no modo calendário */}
            <div className={`${style.booking_card} ${selectedUser ? style.booking_card_calendar : ''}`}>

                {/* Cabeçalho do Cartão */}
                <div className={style.header_section}>
                    <div>
                        <h1 className={style.title}>
                            {selectedUser ? `Agenda de ${selectedUser.fullname.split(' ')[0]}` : 'Agendamento'}
                        </h1>
                        <span className={style.subtitle}>
                            {selectedUser
                                ? 'Selecione um horário disponível abaixo'
                                : 'Escolha um profissional para ver a agenda'}
                        </span>
                    </div>

                    {!selectedUser && (
                        <div className={style.search_container}>
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className={style.search_input}
                            />
                            <svg className={style.search_icon} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                        </div>
                    )}

                    {selectedUser && (
                        <button onClick={handleBackToGrid} className={style.back_button}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="19" y1="12" x2="5" y2="12"></line>
                                <polyline points="12 19 5 12 12 5"></polyline>
                            </svg>
                            Voltar
                        </button>
                    )}
                </div>

                {/* Conteúdo: Grid de Usuários ou Calendário */}
                <div className={style.card_body}>
                    {!selectedUser ? (
                        /* Grelha de Seleção */
                        load.isLoading ? (
                            <div className={style.loading_container}>
                                <div className={style.spinner}></div>
                            </div>
                        ) : (
                            <div className={style.user_grid}>
                                {filteredUsers.map((user, index) => (
                                    <div
                                        key={user.id}
                                        className={style.user_card}
                                        style={{ '--card-index': index }}
                                        onClick={() => handleUserSelect(user)}
                                    >
                                        <Avatar
                                            src={user.avatar}
                                            name={user.fullname}
                                            className={style.user_avatar}
                                            fontSize="24px"
                                        />
                                        <div className={style.user_info}>
                                            <div className={style.user_name}>{user.fullname}</div>
                                            <div className={style.user_role}>{user.role || 'Profissional'}</div>
                                        </div>
                                        <div className={style.card_action}>
                                            Ver Agenda →
                                        </div>
                                    </div>
                                ))}
                                {filteredUsers.length === 0 && (
                                    <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', gridColumn: '1 / -1' }}>
                                        Nenhum profissional encontrado para "{searchTerm}".
                                    </p>
                                )}
                            </div>
                        )
                    ) : (
                        /* NOVO SLOT PICKER */
                        <div className={style.calendar_wrapper}>
                            {load.isLoading ? (
                                <div className={style.loading_container}>
                                    <div className={style.spinner}></div>
                                </div>
                            ) : (
                                <BookingSlotPicker
                                    appointments={appointments}
                                    onSlotSelect={handleSlotSelect}
                                />
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Novo Overlay de Confirmação */}
            <BookingConfirmationOverlay
                isOpen={modalState.isOpen}
                onClose={closeModal}
                onConfirm={handleConfirmBooking}
                services={servicesList}
                data={{
                    date: modalState.date,
                    time: modalState.time,
                    professional: selectedUser
                }}
            />
        </div>
    );
}

export default TelaCliente;
